import type { Game } from '../core/game';
import { BALANCE } from '../data/balance';
import { isEndless, worldName, worldOf } from '../core/world';

/**
 * Adaptateur générique « façade `Game` » — v0.5.0.
 *
 * Remplace `ui/prestigeApi.ts` (supprimé avec le prestige, COMMENTS v0.4.1
 * §1.2) et garde le même contrat : **rien** de ce que livre l'agent `src/core`
 * n'est supposé présent. Chaque accès passe par une fonction gardée qui renvoie
 * une valeur neutre quand la méthode n'existe pas encore, de sorte que
 * `src/ui` compile, se construit et garde ses e2e verts avant comme après la
 * livraison du cœur.
 *
 * Aucun type n'est importé de `core/types` ici : les formes lues (contenant,
 * combat) sont redéclarées structurellement pour survivre à un renommage.
 */

/** Une entrée de `game.containerSummary()` (dés Basiques regroupés). */
export interface ContainerEntryLike {
  sides: number;
  rarity?: string;
  count: number;
}

type AnyFn = (...args: never[]) => unknown;

function record(game: Game): Record<string, unknown> {
  return game as unknown as Record<string, unknown>;
}

/** La façade expose-t-elle cette méthode ? */
export function hasMethod(game: Game, name: string): boolean {
  return typeof record(game)[name] === 'function';
}

/** Appelle `game[name](...args)` ; renvoie `undefined` si absente ou en erreur. */
function call(game: Game, name: string, ...args: unknown[]): unknown {
  const fn = record(game)[name];
  if (typeof fn !== 'function') return undefined;
  try {
    return (fn as AnyFn).apply(game, args as never[]);
  } catch {
    return undefined;
  }
}

/** Appel gardé d'une méthode de la façade (A94) : `undefined` si absente ou en erreur. */
export function callGame(game: Game, name: string, ...args: unknown[]): unknown {
  return call(game, name, ...args);
}

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

// ──────────────────────────────────────────────────────────── Arbre / état

/** Niveau possédé d'un nœud de l'arbre de talents (0 si inconnu). */
export function nodeLevel(game: Game, id: string): number {
  const levels = (game.state as unknown as { nodes?: Record<string, number> }).nodes;
  return num(levels?.[id]);
}

// ───────────────────────────────────────────────────────────── Mode infini

/**
 * Nom du monde du niveau courant **en mode infini** (phase 5, A82) — celui de
 * la zone tirée, ou « Terres mêlées » pour un monde procédural ; `null` en
 * campagne (l'en-tête de l'Accalmie n'y change pas).
 */
export function endlessWorldName(game: Game): string | null {
  const s = game.state;
  if (!isEndless(s.level)) return null;
  return worldName(worldOf(s));
}

// ──────────────────────────────────────────────────────────────── Invasion

/** Le cœur expose-t-il le déclenchement anticipé de l'Invasion (§11.1.4) ? */
export function hasEarlyInvasionApi(game: Game): boolean {
  return hasMethod(game, 'startInvasionEarly');
}

export function startInvasionEarly(game: Game): boolean {
  if (!hasMethod(game, 'startInvasionEarly')) return false;
  return call(game, 'startInvasionEarly') !== false;
}

// ─────────────────────────────────────────────────────────────── Contenant

/**
 * Le Gobelet existe-t-il ? (`game.hasContainer()`, repli sur `state.container`)
 * Depuis la v0.5.0 le contenant vient du **talent** *Gobelet* (`ars_goblet`) :
 * il survit donc aux runs comme le reste de l'arbre.
 */
export function hasContainer(game: Game): boolean {
  if (hasMethod(game, 'hasContainer')) return call(game, 'hasContainer') === true;
  const dice = (game.state as unknown as { container?: unknown }).container;
  return Array.isArray(dice) && dice.length > 0;
}

export function containerCapacity(game: Game): number {
  if (hasMethod(game, 'containerCapacity')) {
    const n = call(game, 'containerCapacity');
    if (typeof n === 'number' && Number.isFinite(n)) return n;
  }
  const derived = game.getDerivedStats() as unknown as { containerCapacity?: number };
  return num(derived.containerCapacity);
}

/**
 * Synthèse du contenant (dés identiques regroupés), du plus fort au plus
 * faible. Repli : regroupement local de `state.container` (BasicDie[]).
 */
export function containerSummary(game: Game): ContainerEntryLike[] {
  let entries: ContainerEntryLike[] | null = null;
  if (hasMethod(game, 'containerSummary')) {
    const out = call(game, 'containerSummary');
    if (Array.isArray(out)) entries = out as ContainerEntryLike[];
  }
  if (!entries) {
    const dice = (game.state as unknown as { container?: { sides?: number }[] }).container;
    if (!Array.isArray(dice)) return [];
    const bySides = new Map<number, ContainerEntryLike>();
    for (const die of dice) {
      const sides = num(die?.sides, 4);
      const found = bySides.get(sides);
      if (found) found.count += 1;
      else bySides.set(sides, { sides, count: 1 });
    }
    entries = [...bySides.values()];
  }
  return entries
    .filter((e) => e && Number.isFinite(e.sides))
    .slice()
    .sort((a, b) => b.sides - a.sides);
}

// ──────────────────────────────────────────────────────── Préparation (A100-2)

/**
 * Renfort de *Préparation* (`DerivedStats.prepBoost`, 0,1 par niveau) en
 * **fraction** (0,2 = +20 %). `null` tant que le cœur A100 ne le publie pas.
 */
export function prepBoost(game: Game): number | null {
  const raw = (game.getDerivedStats() as unknown as Record<string, unknown>).prepBoost;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

// ─────────────────────────────────────────────────────────────── Événements

/**
 * Abonnement à un événement que `GameEvents` ne connaît peut-être pas encore.
 * `Game.on` accepte n'importe quelle clé à l'exécution (simple `Map`) : le cast
 * est sûr, et l'abonnement devient actif dès que le cœur émet l'événement.
 */
export function onGameEvent(game: Game, event: string, fn: () => void): () => void {
  const on = (game as unknown as { on?: (ev: string, f: () => void) => () => void }).on;
  if (typeof on !== 'function') return () => undefined;
  try {
    const off = on.call(game, event, fn);
    return typeof off === 'function' ? off : () => undefined;
  } catch {
    return () => undefined;
  }
}

// ──────────────────────────────────────────────── Chrono de partie (v0.7.0)

/**
 * Temps de jeu cumulé, en secondes (`state.stats.playSeconds`, COMMENTS
 * v0.6.0 §2 / DECISIONS v0.7.0). `null` tant que le cœur ne le publie pas :
 * l'interface n'affiche alors aucun compteur plutôt qu'un « 00:00:00 » figé.
 */
export function playSeconds(game: Game): number | null {
  const stats = (game.state as unknown as { stats?: Record<string, unknown> }).stats;
  const raw = stats?.playSeconds;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

/** Le cœur compte-t-il le temps passé hors combat (`game.addMenuTime`) ? */
export function hasMenuTimeApi(game: Game): boolean {
  return hasMethod(game, 'addMenuTime');
}

/**
 * Fait avancer le chrono de partie pendant qu'un écran **hors combat** est
 * affiché : la vue de combat fait déjà avancer `game.tick(dt)` pendant les
 * phases jouées, et c'est elle qui compte les secondes d'Expédition.
 */
export function addMenuTime(game: Game, dt: number): void {
  call(game, 'addMenuTime', dt);
}

// ─────────────────────────────────────────────────────────── Larbin (v0.7.0)

/** Le talent *Larbin* (`arc_minion`) est-il débloqué ? (`game.hasMinion()`) */
export function hasMinion(game: Game): boolean {
  if (hasMethod(game, 'hasMinion')) return call(game, 'hasMinion') === true;
  return false;
}

/** Le Larbin lance-t-il en ce moment ? (`state.minionEnabled`, défaut : oui) */
export function minionEnabled(game: Game): boolean {
  const raw = (game.state as unknown as { minionEnabled?: unknown }).minionEnabled;
  return typeof raw === 'boolean' ? raw : true;
}

/** Active / désactive le Larbin (`game.setMinionEnabled(on)`). */
export function setMinionEnabled(game: Game, on: boolean): void {
  call(game, 'setMinionEnabled', on);
}

// ───────────────────────────────────────── Premiers / ENFER (combat, v0.7.0)

/**
 * File des multiplicateurs de *Premiers* (`state.combat.primesQueue`) : les
 * prochains lancers sont multipliés par 2, puis 3, puis 5 (COMMENTS v0.6.0
 * §1.2.2). Tableau vide quand le cœur ne publie rien.
 */
export function primesQueue(game: Game): number[] {
  const combat = (game.state as unknown as { combat?: Record<string, unknown> }).combat;
  const raw = combat?.primesQueue;
  if (!Array.isArray(raw)) return [];
  return raw.filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
}

/**
 * État *666 · ENFER* (`state.combat.inferno`) : le sol brûle jusqu'à la fin de
 * l'Expédition, les dégâts sont multipliés. Le cœur peut publier un booléen
 * (actif / inactif) ou directement le multiplicateur — les deux sont lus.
 */
/** Multiplicateur de l'Enfer, lu dans l'équilibrage (repli : ×1,5). */
function infernoMultiplier(): number {
  const raw = (BALANCE as unknown as Record<string, unknown>).spellInfernoMultiplier;
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : 1.5;
}

// ───────────────────────────────────────────── Assommement (combat, v0.8.0)

/**
 * Le joueur est-il **assommé** (Orc Guerrier, Gros Orc, Sirène — A29) ? Plus
 * aucun lancer n'est alors possible, manuel comme *Larbin*. `state.stunRemaining`
 * est déjà public (COMMENTS v0.7.0 §F) : c'est la source la plus directe, avec
 * un repli sur une éventuelle méthode `game.isStunned()` si le cœur en ajoute
 * une plus tard.
 */
export function isStunned(game: Game): boolean {
  const remaining = (game.state as unknown as { stunRemaining?: unknown }).stunRemaining;
  if (typeof remaining === 'number' && Number.isFinite(remaining)) return remaining > 0;
  if (hasMethod(game, 'isStunned')) return call(game, 'isStunned') === true;
  return false;
}

// ───────────────────────────────────────────── Abandon de run (v0.8.0, A30)

/** Le cœur expose-t-il l'abandon de run (`game.abandonRun()`) ? */
export function hasAbandonRun(game: Game): boolean {
  return hasMethod(game, 'abandonRun');
}

/**
 * Abandonne la run en cours (bouton du HUD de combat — A30) : vaut défaite,
 * l'or et les dés de la run sont perdus. Renvoie `false` hors combat ou si le
 * cœur n'expose pas encore la méthode.
 */
export function abandonRun(game: Game): boolean {
  if (!hasMethod(game, 'abandonRun')) return false;
  return call(game, 'abandonRun') !== false;
}

export function inferno(game: Game): { active: boolean; multiplier: number } {
  const combat = (game.state as unknown as { combat?: Record<string, unknown> }).combat;
  const raw = combat?.inferno;
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return { active: raw > 1, multiplier: raw };
  }
  if (typeof raw === 'boolean') return { active: raw, multiplier: infernoMultiplier() };
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    const mult = num(o.multiplier, num(o.mult, infernoMultiplier()));
    const active = o.active === undefined ? true : o.active === true;
    return { active, multiplier: mult };
  }
  return { active: false, multiplier: 1 };
}

// ───────────────────────────────────────────── Mur payant (A112, v0.55.0)

/**
 * État du mur tel que le cœur le publie (`game.gateStatus()`), redéclaré
 * structurellement comme le reste de ce fichier : l'interface compile et reste
 * verte avant comme après la fusion du cœur A112.
 */
export interface GateStatusView {
  /** Dernier niveau gratuit (`FREE_LEVELS`, 2). */
  freeLevels: number;
  /** Jeu acheté (ou code, ou debug). */
  entitled: boolean;
  /** Le boss du dernier niveau gratuit est battu : le niveau suivant est dû. */
  reached: boolean;
  /** Le joueur est bloqué par le mur. */
  locked: boolean;
}

const GATE_METHODS = ['setEntitled', 'isEntitled', 'gateStatus', 'openGate', 'dismissGate', 'claimGate'] as const;

/** Le cœur expose-t-il les six méthodes du mur payant ? Sinon rien ne change à l'écran. */
export function hasGateApi(game: Game): boolean {
  return GATE_METHODS.every((name) => hasMethod(game, name));
}

/** `game.gateStatus()` lu avec prudence ; `null` sans l'API ou si la réponse est illisible. */
export function gateStatus(game: Game): GateStatusView | null {
  if (!hasGateApi(game)) return null;
  const raw = call(game, 'gateStatus');
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  return {
    freeLevels: num(r.freeLevels, 2),
    entitled: r.entitled === true,
    reached: r.reached === true,
    locked: r.locked === true,
  };
}

/** Le jeu est-il ouvert ? Sans l'API, oui (rien n'est jamais verrouillé). */
export function isEntitled(game: Game): boolean {
  if (!hasGateApi(game)) return true;
  return call(game, 'isEntitled') === true;
}

/** Pose l'état « acheté » du cœur (`game.setEntitled`). */
export function setEntitled(game: Game, on: boolean): void {
  call(game, 'setEntitled', on);
}

/** Accalmie → écran d'achat (`game.openGate()`), `false` si le joueur n'est pas bloqué. */
export function openGate(game: Game): boolean {
  return call(game, 'openGate') === true;
}

/** Écran d'achat → Accalmie, même niveau (`game.dismissGate()`). */
export function dismissGate(game: Game): boolean {
  return call(game, 'dismissGate') === true;
}

/** Jeu acheté et niveau dû : passage au niveau suivant (`game.claimGate()`). */
export function claimGate(game: Game): boolean {
  return call(game, 'claimGate') === true;
}

/** Phase courante en chaîne : `'gate'` n'existe dans `Phase` qu'avec le cœur A112. */
export function phaseName(game: Game): string {
  return String(game.state.phase);
}
